import { Component, Input, ChangeDetectionStrategy, SimpleChanges, OnChanges, EventEmitter, Output, ElementRef, ViewChild, HostListener, AfterViewInit, OnDestroy, NgZone, DestroyRef } from '@angular/core';
import { SafeResourceUrl, DomSanitizer, Meta } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LocalstorageHelper, MobileHelper, TabSocket, Message, TabMessagesHelper } from '@nts/std/utility';
import { RoutingService } from '../../routing/routing.service';
import { LoaderComponent } from '../shared/loader/loader.component';

import { filter, Observable, Subject } from 'rxjs';
import { IframeLoadedEvent, StatusMessageInterface } from '@nts/std/interfaces';
import { MessageType } from '@nts/std/types';

@Component({
    selector: 'nts-iframe',
    templateUrl: './iframe.component.html',
    styleUrls: ['./iframe.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    LoaderComponent
]
})
export class IframeComponent implements OnChanges, AfterViewInit, OnDestroy {

  @Input() url: string;
  @Input() allow: boolean = false;
  @Input() sandbox: boolean = false;
  @Input() statusCheckMode: 'polling' | 'push' | 'both' = 'push';
  @Input() pollIntervalMs: number = 5000;
  @Input() enableHealthCheck: boolean = false;
  @Input() healthCheckIntervalMs: number = 10000;

  @Output()
  loaded = new EventEmitter<IframeLoadedEvent>();

  @Output()
  onChildReadyForMessages = new EventEmitter<IframeLoadedEvent>();

  /**
   * Emitted when a message is received from the iframe
   */
  @Output()
  messageReceived = new EventEmitter<Message<any>>();

  /**
   * Emitted when status changes (either via polling or push)
   */
  @Output()
  statusChanged = new EventEmitter<StatusMessageInterface>();

  /**
   * Native element da l'accesso al native element dell'iframe
   */
  @ViewChild('iframe', { static: false })
  private _nativeElement: ElementRef;

  private destroy$ = new Subject<void>();
  private statusCheckInterval: any = null;
  private healthCheckInterval: any = null;
  private lastStatus: StatusMessageInterface | null = null;
  private tabSocket: TabSocket | null = null;
  private initializedIframeWindow: Window | null = null;
  private initializedIframeUrl: string | null = null;
  private frameInstanceId = crypto.randomUUID();

  socket: TabSocket;

  get nativeElement(): ElementRef {
    return this._nativeElement?.nativeElement;
  }

  iframeLoaded = false;
  urlSafe: SafeResourceUrl;

  constructor(
    private readonly sanitizer: DomSanitizer,
    private routingService: RoutingService,
    private meta: Meta,
    private zone: NgZone,
    private destroyRef: DestroyRef
  ) {

  }

  ngOnDestroy(): void {
    MobileHelper.zoomEnable(this.meta);
    this.destroy$.next();
    this.destroy$.complete();
    if (this.statusCheckInterval) {
      clearInterval(this.statusCheckInterval);
    }
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
    }
    if (this.tabSocket) {
      this.tabSocket.destroy();
      this.tabSocket = null;
    }
    this.initializedIframeWindow = null;
    this.initializedIframeUrl = null;
  }

  ngAfterViewInit(): void {
    MobileHelper.zoomDisable(this.meta);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['url'].currentValue !== changes['url'].previousValue) {
      if (this.statusCheckInterval) {
        clearInterval(this.statusCheckInterval);
        this.statusCheckInterval = null;
      }
      if (this.healthCheckInterval) {
        clearInterval(this.healthCheckInterval);
        this.healthCheckInterval = null;
      }
      if (this.tabSocket) {
        this.tabSocket.destroy();
        this.tabSocket = null;
      }
      this.initializedIframeWindow = null;
      this.initializedIframeUrl = null;
      this.lastStatus = null;

      this.iframeLoaded = false;
      this.urlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(this.url);
      this.iframeLoaded = true;
    }
  }

  @HostListener('window:blur', ['$event'])
  onWindowBlur(event: any): void {
    this.routingService.triggerIFrameFocusChanged(window);
  }

  load(iframe: HTMLIFrameElement): void {
    const iframeWindow: Window = iframe.contentWindow;
    const iframeUrl = iframe.src || this.url;

    if (this.initializedIframeWindow === iframeWindow && this.initializedIframeUrl === iframeUrl) {
      return;
    }

    if (this.tabSocket) {
      this.tabSocket.destroy();
    }

    this.initializedIframeWindow = iframeWindow;
    this.initializedIframeUrl = iframeUrl;
    LocalstorageHelper.startListener(iframeWindow);

    // Create TabSocket for communication
    this.tabSocket = new TabSocket(this.zone, iframeWindow);
    this.socket = this.tabSocket;

    let initializedChild = false;

    // Setup message routing
    this.tabSocket.onMessage()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        // Devo ignorare i messaggi di tipo AnswerStatus perché sono gestiti con i messaggi dedicati che attendono la risposta
        filter(message => message.type !== MessageType.AnswerStatus && message.type !== MessageType.AnswerPendingChanges)
      )
      .subscribe((message) => {
        if (message.type === MessageType.HealthCheck) {
          if (!initializedChild) {
            initializedChild = true;
            this.onChildReadyForMessages.emit({
              iframe: iframe,
              frameInstanceId: this.frameInstanceId,
              tabSocket: this.tabSocket,
            });
          }
        } else {
          this.messageReceived.emit(message);
        }
      });

    // Setup status push listener if enabled
    if (this.statusCheckMode === 'push' || this.statusCheckMode === 'both') {
      this.tabSocket.onStatusPush()
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          filter(status => status !== null)
        )
        .subscribe((status: StatusMessageInterface) => {
          this.lastStatus = status;
          this.statusChanged.emit(status);
        });
    }

    // Setup polling if enabled
    if (this.statusCheckMode === 'polling' || this.statusCheckMode === 'both') {
      this.startPolling();
    }

    // Setup health check if enabled
    if (this.enableHealthCheck) {
      this.startHealthCheck();
    }

    this.loaded.emit({
      iframe: iframe,
      frameInstanceId: this.frameInstanceId,
      tabSocket: this.tabSocket,
    });
  }

  /**
   * Start polling for status changes
   * @private
   */
  private startPolling(): void {
    if (this.statusCheckInterval) {
      clearInterval(this.statusCheckInterval);
    }

    this.statusCheckInterval = setInterval(async () => {
      const status = await this.checkStatus();
      // Emit only if status changed
      if (!this.lastStatus || JSON.stringify(this.lastStatus) !== JSON.stringify(status)) {
        this.lastStatus = status;
        this.statusChanged.emit(status);
      }
    }, this.pollIntervalMs);
  }

  /**
   * Start health check (heartbeat) for connection validation
   * Periodically validates that the iframe connection is still active
   * @private
   */
  private startHealthCheck(): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
    }

    this.healthCheckInterval = setInterval(async () => {
      await this.validateConnection();
    }, this.healthCheckIntervalMs);
  }

  /**
   * Validate the iframe connection is still active
   * Sends a HealthCheck message and awaits response
   * @returns Promise<boolean> true if connection is valid, false if failed
   */
  async validateConnection(): Promise<boolean> {
    if (!this.tabSocket) {
      return false;
    }

    try {
      const response = await this.tabSocket.onMessageOnce(MessageType.HealthCheck);
      // Send heartbeat message
      this.tabSocket.sendCustomMessage({ timestamp: Date.now() }, MessageType.HealthCheck);
      return !!response;
    } catch (err) {
      console.warn('Health check failed:', err);
      return false;
    }
  }

  /**
   * Check the current status from the iframe
   * @returns Promise with status data
   */
  async checkStatus(): Promise<StatusMessageInterface> {
    if (!this.tabSocket) {
      return {
        hasModalOpen: false,
        pendingChanges: false,
        version: 0
      };
    }

    try {
      return await TabMessagesHelper.checkStatus(this.tabSocket);
    } catch (err) {
      console.error('Error checking iframe status:', err);
      return {
        hasModalOpen: false,
        pendingChanges: false,
        version: 0
      };
    }
  }

  /**
   * Send a custom message to the iframe
   * @param data The message data
   * @param messageType Optional message type
   */
  send<T = any>(data: T, messageType: MessageType = MessageType.CustomMessage): void {
    if (this.tabSocket) {
      this.tabSocket.sendCustomMessage(data, messageType);
    }
  }

  /**
   * Send a message to the iframe and wait for response
   * @param data The request data
   * @param messageType Optional message type
   * @returns Promise with response data
   */
  async sendAsync<TReq, TRes = any>(data: TReq, messageType?: MessageType): Promise<TRes> {
    if (!this.tabSocket) {
      throw new Error('TabSocket not initialized');
    }
    return this.tabSocket.sendMessageAsync<TReq, TRes>(data, messageType);
  }

  /**
   * Listen for messages of a specific type
   * @param messageType Optional filter by message type
   * @returns Observable of message data
   */
  onMessage<T = any>(messageType?: MessageType): Observable<Message<T>> {
    if (!this.tabSocket) {
      return new Observable(observer => observer.complete());
    }
    return this.tabSocket.onMessage<T>(messageType);
  }

  /**
   * Get the underlying TabSocket instance for advanced use cases
   * @returns The TabSocket instance or null if not initialized
   */
  getSocket(): TabSocket | null {
    return this.tabSocket;
  }
}
